import { useEffect, useRef, useState } from 'react';
import { useTenantParams } from '@/hooks/use-tenant-params';
import { locationApi } from '@/lib/api-paths';
import { ArrowUpRight, Loader2 } from 'lucide-react';
import { TERMS } from '@pageboss/shared';
import { api, type ApiError } from '@/lib/api';
import type { GbpLocationCandidate } from '@/lib/types';
import { cn } from '@/lib/utils';

// The server's failure codes, in the product's voice. Without this the catch
// below fell through to `e.message`, which for a non-2xx without a `message`
// field is lib/api.ts's raw token — so a business owner could be shown the
// literal string "invalid_candidate". Only `already_connected` ships a
// human-readable `message` from the server (it has to: it may name one of
// the customer's own sites), and that one is preferred over this table.
const SELECT_ERROR_COPY: Record<string, string> = {
  invalid_challenge:
    `This connection attempt is no longer valid — it may have been open too long. Start again from your ${TERMS.googleProfile} screen.`,
  invalid_candidate:
    "We couldn't match that choice to the list we got from Google. Reload this screen and try again.",
  invalid_input: 'Something went wrong sending that choice. Please try again.',
  gbp_unavailable: `Connecting a ${TERMS.googleProfile} isn't switched on right now.`,
  not_found: 'We could not find this site. Reload this screen and try again.',
  // 403 from the owner-only `gbp:manage` gate (P2.3 policy row; enforced in production
  // since the 2026-09-12 hotfix): the caller lost the owner role after this picker was
  // rendered. Retrying cannot help; say who can.
  forbidden: `Only the business owner can connect or change the ${TERMS.googleProfile}. Nothing was changed — ask the owner to connect it.`,
};

// design brief §5.3: a clickable ROW list, not radio buttons. Every row is
// its own full-width button (≥44px tall on mobile) so the tap target is the
// whole row, not a small button at the end of it.
export function LocationPicker({
  candidates,
  onChanged,
}: {
  candidates: GbpLocationCandidate[];
  // The announcement is spoken by the screen's live region, which also moves
  // focus once the picker is replaced by the connected screen — otherwise
  // the row button that was focused is unmounted and focus falls to <body>.
  onChanged: (announcement?: string) => void;
}) {
  // The tenant is the canonical route's, proven by the server on the request
  // that rendered this. It is never inferred from the other identifier.
  const { businessId = '', locationId: siteId = '' } = useTenantParams();
  const [selecting, setSelecting] = useState<string | null>(null);
  const [selectError, setSelectError] = useState<string | null>(null);
  const errorRef = useRef<HTMLParagraphElement | null>(null);

  // Clicking a row disables it, and a browser moves focus to <body> when the
  // focused element becomes disabled — so a keyboard user who hits a
  // recoverable failure was dropped to the top of the document and had to
  // Tab all the way back. That path only became worth walking now that a
  // failed attempt no longer burns the challenge (the server-side fix in
  // this same wave), which is exactly what makes "pick a different one"
  // real advice. Put focus on the message that tells them what to do.
  useEffect(() => {
    if (selectError) errorRef.current?.focus();
  }, [selectError]);

  async function select(resourceName: string) {
    setSelecting(resourceName);
    setSelectError(null);
    try {
      await api.post(locationApi(businessId, siteId, 'gbp/connect/select'), { resourceName });
      onChanged(`${TERMS.googleProfile} connected.`);
    } catch (err) {
      const e = err as ApiError;
      // Prefer the server's own sentence (already_connected sends one, and it
      // may legitimately name one of the customer's other sites); otherwise
      // translate the code rather than printing it.
      const serverMessage =
        e.body && typeof e.body === 'object' && 'message' in e.body
          ? String((e.body as { message: unknown }).message)
          : null;
      const code =
        e.body && typeof e.body === 'object' && 'error' in e.body
          ? String((e.body as { error: unknown }).error)
          : null;
      setSelectError(
        serverMessage ??
          (code && Object.hasOwn(SELECT_ERROR_COPY, code)
            ? (SELECT_ERROR_COPY[code] as string)
            : 'Could not connect that location. Please try again.'),
      );
    } finally {
      setSelecting(null);
    }
  }

  return (
    <div className="rounded-xl border border-border-subtle bg-card p-6">
      <h2 className="text-base font-semibold tracking-tight">Which {TERMS.googleProfile} is this?</h2>
      {/* Two things this copy must NOT say.
          (1) "Your Google account manages more than one Business Profile" —
          the old wording. The picker opens whenever we could not auto-match
          (no captured place_id, or two captures disagreeing), which says
          nothing about how many profiles exist, and it routinely opens with
          exactly one.
          (2) A COUNT of what we found. `candidates` is the union of the
          Google accounts whose listing call happened to succeed; the callback
          skips an account that 429s or times out. Stating "we found 3" when
          the owner has 9 would be asserting a number we cannot back — the
          rule this branch broke three times already. */}
      <p className="mt-1 text-sm text-text-secondary">
        {candidates.length === 1
          ? 'Confirm this is the Business Profile for this site.'
          : 'Pick the Business Profile for this site.'}
      </p>
      {selectError ? (
        <p
          ref={errorRef}
          tabIndex={-1}
          className="mt-3 text-sm text-danger-fg focus:outline-none"
          role="alert"
        >
          {selectError}
        </p>
      ) : null}
      <ul className="mt-4 flex flex-col gap-2">
        {candidates.map((c) => (
          <li key={c.resourceName}>
            <button
              type="button"
              disabled={selecting !== null}
              onClick={() => void select(c.resourceName)}
              className={cn(
                'flex min-h-11 w-full items-center justify-between gap-3 rounded-lg border border-border-subtle',
                'bg-surface-page px-4 py-3 text-left transition-colors',
                'hover:border-border-strong hover:bg-surface-sunken',
                'disabled:cursor-not-allowed disabled:opacity-60',
              )}
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold text-foreground">{c.title}</p>
                {c.addressLine ? (
                  <p className="mt-0.5 truncate text-xs text-text-secondary">{c.addressLine}</p>
                ) : null}
              </div>
              {selecting === c.resourceName ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-text-tertiary" aria-hidden />
              ) : (
                <ArrowUpRight className="h-4 w-4 shrink-0 text-text-tertiary" aria-hidden />
              )}
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
